import { Grid2, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import StatusBox from "../../../components/StatusBox";
import { useTranslate } from "../../../hooks/useTranslate";
import { useStudents } from "../../../context/students";
import SummaryBox from "../../../components/SummaryBox";
import SchoolAddressForm from "./components/SchoolAddressForm";
import OptionsToggle from "../../../components/OptionsToggle";
import HomeSchoolForm from "./components/HomeSchoolForm";
import { useGetPreviousEducation } from "./hooks/useGetPreviousEducation";
import axiosManager from "../../../services/axiosManager";
import SnackbarToast from "../../../components/SnackbarToast";
import Preloader from "../../../components/Preloader";
import useStatuses from "../../../context/status/useStatuses";
import VerificationStatusChip from "../../../components/VerificationStatusChip";
import { isVersionUK } from "../../../services/versionService";
import { KEY_APPROVED, KEY_NULL, KEY_PROGRESS } from "../../../utils/getVerificationStatusKey";

const PreviousEducation = () => {
    const { t } = useTranslate();
    const { activeStudentId, activeStudent } = useStudents();
    const {
        previousEducation, isLoading, refetch, isRefetching,
    } = useGetPreviousEducation(activeStudentId);
    const {
        statuses, isLockedStatus, refetchGeneralStatuses, generalStatuses, stepsProgress,
    } = useStatuses();
    const isLocked = isLockedStatus.responsibleAdults;
    const { studyingIb, yearGroup = 0 } = generalStatuses?.student || {};

    const educationStatus = useMemo(() => {
        if (statuses.transcripts.key === KEY_PROGRESS && statuses.previousEducation.key === KEY_APPROVED) {
            return statuses.transcripts;
        }
        if (stepsProgress.previousEducation.completed) {
            return { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };
        }
        return { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true };
    }, [stepsProgress.studentDetails, statuses.studentVerification]);

    const options = [
        {
            value: "PhysicalSchool",
            title: t("previousEducation.mainstreamPhysicalSchool"),
            content: isVersionUK ? t("previousEducation.mainstreamPhysicalSchoolDescription") : undefined,
        },
        {
            value: "HomeSchool",
            title: t("previousEducation.homeBasedSchool"),
            content: isVersionUK ? t("previousEducation.homeBasedSchoolDescription") : undefined,
        },
    ];

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [schoolType, setSchoolType] = useState(previousEducation.previousInstitutionType || options[0].value);

    useEffect(() => {
        if (previousEducation.previousInstitutionType) {
            setSchoolType(previousEducation.previousInstitutionType);
        }
    }, [previousEducation.previousInstitutionType]);

    const {
        mutateAsync: updateEducation, reset: resetUpdateEducation,
        isPending: isUpdatingEducation, isSuccess: isSuccessUpdateEducation, error: errorUpdateEducation,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${activeStudentId}/PreviousEducation`, body),
    });
    const {
        mutateAsync: uploadTranscripts, reset: resetUploadTranscripts,
        isPending: isUploadingTranscripts, isSuccess: isSuccessUploadTranscripts, error: errorUploadTranscripts,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${activeStudentId}/PreviousEducation/transcript`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });
    const {
        mutateAsync: uploadIBApplication, reset: resetUploadIBApplication,
        isPending: isUploadingIBApplication, isSuccess: isSuccessUploadIBApplication, error: errorUploadIBApplication,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${activeStudentId}/PreviousEducation/IBApplication`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });
    const {
        mutateAsync: uploadIBGradeEvidence, reset: resetUploadIBGradeEvidence,
        isPending: isUploadingIBGradeEvidence, isSuccess: isSuccessUploadIBGradeEvidence, error: errorUploadIBGradeEvidence,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${activeStudentId}/PreviousEducation/IBGradeEvidence`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });

    const onUpdateEducation = async ({
        transcripts, applicationLetter, mockResults, ...formData
    }) => {
        setIsSubmitted(true);

        const isSchoolReportsRequired = isVersionUK && ([11, 13].includes(yearGroup) || !!studyingIb);
        const isOtherFilesRequired = isVersionUK && !!studyingIb;
        const hasReports = statuses.transcripts.isSuccess || statuses.transcripts.isInfo;

        if (!hasReports && isSchoolReportsRequired && transcripts.length === 0) return;
        if (!hasReports && isOtherFilesRequired && ((applicationLetter && applicationLetter.length === 0) || (mockResults && mockResults.length === 0))) return;

        try {
            await updateEducation({ ...formData, previousInstitutionType: schoolType });

            if (transcripts.length > 0) {
                const formDataBody = new FormData();
                transcripts.forEach((file) => { formDataBody.append("file", file); });
                await uploadTranscripts(formDataBody);
            }

            if (applicationLetter && applicationLetter.length > 0) {
                const formDataBody = new FormData();
                applicationLetter.forEach((file) => { formDataBody.append("file", file); });
                await uploadIBApplication(formDataBody);
            }

            if (mockResults && mockResults.length > 0) {
                const formDataBody = new FormData();
                mockResults.forEach((file) => { formDataBody.append("file", file); });
                await uploadIBGradeEvidence(formDataBody);
            }
            refetch();
            refetchGeneralStatuses();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="page-content">
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Typography variant="h1">
                        {t("previousEducation.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4" component="div">
                        {t("previousEducation.description")}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <StatusBox
                        title={t("previousEducation.previousEducationStatus")}
                        isSuccess={educationStatus.isSuccess}
                        isWarning={educationStatus.isWarning}
                        isInfo={educationStatus.isInfo}
                    >
                        <Typography variant="body2">
                            {t("previousEducation.previousEducationStatusDescription")}
                        </Typography>
                        <VerificationStatusChip status={educationStatus} />
                    </StatusBox>
                </Grid2>
            </Grid2>
            <Preloader isLoading={isLoading}>
                <Preloader isLoading={isRefetching} overflow showInitiallyOverflow>
                    <OptionsToggle
                        label={t("previousEducation.attendedA")}
                        options={options}
                        value={schoolType}
                        onChange={setSchoolType}
                        className="mt-12 mb-12"
                        disabled={isLocked}
                    />
                    <SummaryBox
                        className="mb-12"
                        contentClassName="p-8"
                        isFormBox
                    >
                        {schoolType === "PhysicalSchool" && (
                            <SchoolAddressForm
                                isSubmitted={isSubmitted}
                                data={previousEducation}
                                isDisabled={isLocked}
                                isSaving={isUpdatingEducation || isUploadingTranscripts || isUploadingIBApplication || isUploadingIBGradeEvidence}
                                onSave={onUpdateEducation}
                            />
                        )}
                        {schoolType === "HomeSchool" && (
                            <HomeSchoolForm
                                data={previousEducation}
                                isDisabled={isLocked}
                                isSaving={isUpdatingEducation || isUploadingTranscripts || isUploadingIBApplication || isUploadingIBGradeEvidence}
                                onSave={onUpdateEducation}
                            />
                        )}
                    </SummaryBox>
                </Preloader>
            </Preloader>
            <SnackbarToast
                open={isSuccessUpdateEducation || !!errorUpdateEducation}
                isSuccess={isSuccessUpdateEducation}
                isError={errorUpdateEducation}
                onClose={resetUpdateEducation}
            >
                {isSuccessUpdateEducation
                    ? t("previousEducation.form.successUpdateMessage")
                    : `${t("previousEducation.form.errorUpdateMessage")} (status ${errorUpdateEducation?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUploadTranscripts || !!errorUploadTranscripts}
                isSuccess={isSuccessUploadTranscripts}
                isError={errorUploadTranscripts}
                onClose={resetUploadTranscripts}
            >
                {isSuccessUploadTranscripts
                    ? t("previousEducation.form.successUploadTranscriptsMessage")
                    : `${t("previousEducation.form.errorUploadTranscriptsMessage")} (status ${errorUploadTranscripts?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUploadIBApplication || !!errorUploadIBApplication}
                isSuccess={isSuccessUploadIBApplication}
                isError={errorUploadIBApplication}
                onClose={resetUploadIBApplication}
            >
                {isSuccessUploadIBApplication
                    ? t("previousEducation.form.successUploadIBApplicationMessage")
                    : `${t("previousEducation.form.errorUploadIBApplicationMessage")} (status ${errorUploadIBApplication?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUploadIBGradeEvidence || !!errorUploadIBGradeEvidence}
                isSuccess={isSuccessUploadIBGradeEvidence}
                isError={errorUploadIBGradeEvidence}
                onClose={resetUploadIBGradeEvidence}
            >
                {isSuccessUploadIBGradeEvidence
                    ? t("previousEducation.form.successUploadIBGradeEvidenceMessage")
                    : `${t("previousEducation.form.errorUploadIBGradeEvidenceMessage")} (status ${errorUploadIBGradeEvidence?.response?.status})`}
            </SnackbarToast>
        </div>
    );
};

export default PreviousEducation;
